import React, { useMemo } from 'react'
import cx from 'classnames'
import { getAnalyticsContext, track } from 'analytics'
import { processError } from 'helpers'
import { openNotification } from 'notifications'
import { useCartContext } from 'modules/cart'
import { Icon, Text } from 'components/dataDisplay'


type OrderItemQuantityProps = {
  className?: string
  buttonClassName?: string
  productLineItemUid: string
  value: number
  metadata: Analytics.Events['Cart product remove']
}

const OrderItemQuantity: React.FunctionComponent<OrderItemQuantityProps> = (props) => {
  const { className, buttonClassName, productLineItemUid, value, metadata } = props

  const { modifyCart } = useCartContext()
  const [ cartModify, { isFetching } ] = modifyCart

  const [ decrease, increase ] = useMemo(() => {
    const handleChange = (delta: number) => async () => {
      try {
        const { page, pageType } = getAnalyticsContext()

        const { error } = await cartModify({
          productItems: [
            {
              productLineItemUid,
              quantity: value + delta,
            },
          ],
          metadata: {
            page,
            pageType,
            placement: 'Cart',
          },
        })

        if (error?.__typename === 'CartError' && error?.cartErrorCode === 'LEP_COUNT_ISSUE') {
          openNotification('error', {
            message: error.message,
          })
        }

        if (value + delta <= 0) {
          track('Cart product remove', metadata)
        }
      }
      catch (error) {
        processError(error)
      }
    }

    return [ handleChange(-1), handleChange(1) ]
  }, [ cartModify, productLineItemUid, value, metadata ])

  const buttonClassname = cx('size-s24 cursor-pointer rounded-full bg-pink disabled:opacity-50', buttonClassName)

  return (
    <div
      className={cx(className, 'inline-flex items-center justify-start text-center')}
      aria-busy={isFetching}
      data-testid="quantity"
    >
      <button className={buttonClassname} type="button" onClick={decrease} disabled={isFetching} data-testid="decrease">
        <Icon name="24/minus" />
      </button>
      <Text className={cx('mx-12 min-w-[20rem]')} style="h8" message={value.toString()} data-testid="orderItemCount" />
      <button className={buttonClassname} type="button" onClick={increase} disabled={isFetching} data-testid="increase">
        <Icon name="24/plus" />
      </button>
    </div>
  )
}


export default OrderItemQuantity
